import React, {useMemo} from 'react';
import {useMutation} from '@apollo/client';
import {Box, Divider, Grid, Typography} from '@mui/material';
import {Form, FormikErrors} from 'formik';
import {AtrFormValues} from './AtrFields';
import AtrSpeedometer from './AtrSpeedometer';
import Loading from '../Loading/Loading';
import {ErrorPage} from '../../pages/ErrorPage';
import AtrInputs from './AtrInputs';
import withAutoFormNew from '../AutoForm/AutoFormNew';
import {newUTCDate} from '../../utils/newUTCDate';
import {useATR} from './useATR';
import {LATEST_ATR} from './AtrQueries';
import {AtrFragment} from '../../schema-types/AtrFragment';
import {ReviewDetailsFragment_contacts} from '../../schema-types/ReviewDetailsFragment';
import {ATRNaming, useRTQ} from '../../hooks/useRTQ';
import validateUUID from '../../utils/validateUUID';
import {PowerUserDeleteAction} from '../PowerUserDelete/PowerUserDeleteAction';
import {DELETE_PENDING_RTQ} from '../PowerUserDelete/powerUserQueries';
import {
  DeletePendingRtq,
  DeletePendingRtqVariables,
} from '../../schema-types/DeletePendingRtq';

const classes = {
  root: {
    width: '80%',
    margin: 'auto',
    paddingTop: '1rem',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  centeredText: {
    fontWeight: 'bold',
    fontSize: '14px',
    padding: 'auto',
  },
  mainATR: {
    height: '10rem',
    width: '17rem',
  },
};

const RatingText: React.FC<{
  clientName: string;
  atrScore: number | null;
  manualAtrScore: number | null;
  isPendingAtr: boolean;
}> = ({atrScore, clientName, isPendingAtr, manualAtrScore}) => {
  return (
    <>
      {isPendingAtr && (
        <Typography sx={classes.centeredText}>
          {clientName} has not answered their questionnaire yet
        </Typography>
      )}
      <Box mt={1} />
      {manualAtrScore && manualAtrScore !== atrScore && (
        <Typography sx={classes.centeredText}>
          {clientName} has a manually assigned risk score of {manualAtrScore}
        </Typography>
      )}
      <Box mt={1} />
      {atrScore != null && (
        <Typography sx={classes.centeredText}>
          {clientName} has scored a risk score of {atrScore}
        </Typography>
      )}
      <Box mt={2} />
    </>
  );
};

const ScoreDisplay: React.FC<{score?: number; atrName: ATRNaming}> = ({
  score = 0,
  atrName,
}) => {
  if (score === 0) {
    return <Typography variant="h6">{atrName} is pending</Typography>;
  }

  return (
    <Box sx={classes.mainATR}>
      <AtrSpeedometer value={score} />
    </Box>
  );
};

const SingleContactDetails: React.FC<{
  contact: ReviewDetailsFragment_contacts;
  atr?: AtrFragment | null;
}> = ({contact, atr}) => {
  const pendingAtr = atr?.pending;
  const {atrName} = useRTQ();
  const tool = atr?.atrTool;
  const clientName = contact.simplifiedName || 'Client';
  const dateCreated = atr?.dateCreated
    ? newUTCDate(atr?.dateCreated).toLocaleDateString()
    : null;
  const dateFilledIn = atr?.dateSubmitted
    ? newUTCDate(atr?.dateSubmitted).toLocaleDateString()
    : null;

  if (!atr) {
    return (
      <Box sx={classes.centeredText}>
        No current or pending {atrName} for {contact.name}
      </Box>
    );
  }
  return (
    <>
      <Box sx={classes.centeredText}>
        <RatingText
          clientName={clientName}
          atrScore={atr?.value ? Number(atr.value) : null}
          manualAtrScore={atr?.amendedValue ?? null}
          isPendingAtr={!!pendingAtr}
        />
      </Box>
      <Box flex={1} mb={4} width="100%">
        <Grid container>
          <Grid item xs={4}>
            <Typography variant="body1">Tool used:</Typography>
            <Typography variant="h6">{tool}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body1">Date requested:</Typography>
            <Typography variant="h6">{dateCreated}</Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body1">Date filled in:</Typography>
            <Typography variant="h6">{pendingAtr ? 'N/A' : dateFilledIn}</Typography>
          </Grid>
        </Grid>
      </Box>
      <Typography>You can request a new questionnaire using the form below</Typography>
      <Box display="flex" flexDirection="row">
        {atr?.finalValue && <ScoreDisplay score={atr.finalValue} atrName={atrName} />}
      </Box>
    </>
  );
};

const ContactAtrGaugeForm: React.FC<{
  contact: ReviewDetailsFragment_contacts;
  atrName: ATRNaming;
  accountId: string;
}> = ({contact, atrName, accountId}) => {
  const {
    latestATRData: data,
    latestATRLoading: loading,
    latestATRError: error,
  } = useATR(accountId, contact);
  const {canUseRisk} = useRTQ();
  const [onDeletePendingRtq, {loading: onDeletePendingRtqLoading}] = useMutation<
    DeletePendingRtq,
    DeletePendingRtqVariables
  >(DELETE_PENDING_RTQ, {
    refetchQueries: [
      {
        query: LATEST_ATR,
        variables: {
          contactId: validateUUID(contact.id)!,
          accountId: validateUUID(accountId)!,
        },
      },
    ],
    awaitRefetchQueries: true,
  });
  const showManualAmend = useMemo(() => {
    return Boolean(data?.latestAtr && !canUseRisk);
  }, [data, contact, canUseRisk]);

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <ErrorPage code="500" text={`There was an issue with the ${atrName}`} />;
  }

  if (!data) {
    return null;
  }

  const {latestAtr} = data;

  return (
    <>
      <Form>
        <Box sx={classes.root}>
          <SingleContactDetails contact={contact} atr={latestAtr} />
          <Box mt={4} />
          <AtrInputs
            loadingAtr={loading}
            advisers={data.advisers!.length > 0 ? data.advisers! : data.getCurrentUser!}
            showManualAmend={showManualAmend}
          />
        </Box>
        {latestAtr && (
          <Box display="flex" justifyContent="center" mb={2} mt={2}>
            <Divider />
            <PowerUserDeleteAction
              recordLabel={`${atrName} for ${
                contact.simplifiedName || contact.name || 'this client'
              }`}
              isPending={Boolean(latestAtr.pending)}
              loading={onDeletePendingRtqLoading}
              buttonLabel={`Delete ${atrName}`}
              successMessage={`Deleted ${atrName}`}
              defaultErrorMessage={`Unable to delete ${atrName}.`}
              onConfirm={() =>
                onDeletePendingRtq({
                  variables: {atrId: latestAtr.id},
                }).then(({data: result}) => result?.deletePendingRtq ?? null)
              }
            />
          </Box>
        )}
      </Form>
    </>
  );
};

const ContactAtrGaugeAutoForm = withAutoFormNew<
  AtrFormValues,
  {contact: ReviewDetailsFragment_contacts; atrName: ATRNaming; accountId: string}
>(ContactAtrGaugeForm);

const ContactAtrForm: React.FC<{
  contact: ReviewDetailsFragment_contacts;
  close: () => void;
  atrName: ATRNaming;
  accountId: string;
}> = ({contact, close, atrName, accountId}) => {
  const {getSubmitHandler} = useATR(accountId, contact, close);
  return (
    <ContactAtrGaugeAutoForm
      formFields={[]}
      validate={(values: AtrFormValues) => {
        const errors: FormikErrors<AtrFormValues> = {};
        if (!values.adviserId) {
          errors.adviserId = 'Required';
        }
        if (values.action === 'manual') {
          if (!values.amendReason) {
            errors.amendReason = 'Required';
          }
        }
        return errors;
      }}
      atrName={atrName} // or 'ATR' based on your feature flag
      submit={(values: AtrFormValues) => {
        return getSubmitHandler({action: values.action})(values);
      }}
      contact={contact}
      accountId={accountId}
    />
  );
};

export default ContactAtrForm;
